'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import { client, CLIENT_FILES, CLIENT_LOG, teamMember, type ClientFile } from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, Tag } from './app-screen'
import styles from './client-log-visual.module.css'
import { useScenario } from './use-scenario'

// The same dossier `ClientRecordVisual` shows the fields of. That card answers "what is this
// client"; this one answers "what is filed against it, and what did we last do to it" — the half of
// the record a firm actually opens between two pieces of work.
const SUBJECT = client('10010')

/** The log without its newest entry, and that entry. */
const SETTLED_LOG = CLIENT_LOG.slice(1)
const NEWEST_LOG = CLIENT_LOG[0]

/**
 * The second entry that names a document, and the document it names.
 *
 * Two of this dossier's three log lines point at a file on the shelf above them, and the card walks
 * both: the newest lands and its document answers, then the mark travels down to the pair under it.
 * A dossier with one link in it demonstrates a coincidence; a dossier with two demonstrates a rule.
 */
const SECOND_LINKED = SETTLED_LOG.find((entry) => entry.file)

/** How many of the log's lines a phone-width card has room for. The two it drops are the oldest,
 *  and the two the story marks are both at the top. */
const NARROW_ENTRIES = 4

const FILE_COLOR: Record<ClientFile['kind'], string> = {
  pdf: '#c0392b',
  xlsx: '#1d7044',
  docx: '#2b5797',
}

/**
 * A dossier that is already full, and an upload arriving in it — in both places at once.
 *
 * The three blocks used to arrive one per beat and empty again to restart, so BESTANDEN and LOGBOEK
 * sat as labelled headings with nothing underneath them for a good part of every pass. That was
 * fixed by making every block present in every frame, which left the card with one small event in
 * it: a log line dropping in, and a wash moving.
 *
 * An upload is the one kind of entry that puts something on the shelf as well as a line in the log,
 * so it is the one worth watching happen. The document opens onto the shelf and pushes what is
 * under it down, the count beside BESTANDEN goes up, the line that records it lands and the count
 * beside LOGBOEK follows — and then the mark travels to the pair below, the KvK filing and the
 * annual accounts it names. Four things move where one did, and every one of them is the same
 * claim: on this dossier, one act is filed in every place it belongs.
 *
 * The contacts that used to sit above the shelf are gone. They are on the hero of this same page,
 * under a heading of their own, and the two cards were showing the same two people to a reader
 * scrolling past both. What they were taking up is the room the documents now have.
 */
const BEAT_HOLDS = [1500, 700, 1100, 1300, 1200] as const

const REST_BEAT = 0 // SSR / reduced-motion snapshot — the dossier complete, nothing marked
const FILE_LANDS_AT = 2 // the document opens onto the shelf
const ENTRY_LANDS_AT = 3 // the line that records it lands under it
const SECOND_LINK_AT = 4 // and the mark moves on to the pair below

/** One document on the shelf: kind, name, size and the day it was filed. */
function FileRow({ file, fmt, kb }: { file: ClientFile; fmt: ReturnType<typeof useVisualCopy>['fmt']; kb: string }) {
  return (
    <>
      <span
        className="flex h-[18px] w-[27px] shrink-0 items-center justify-center rounded-[4px] text-[7.5px] font-bold text-white uppercase"
        style={{ background: FILE_COLOR[file.kind] }}
      >
        {file.kind}
      </span>
      <span className="min-w-0 flex-1 truncate text-[11px] font-medium">{file.name}</span>
      <span className="shrink-0 text-[9px] whitespace-nowrap tabular-nums" style={{ color: 'var(--au-text2)' }}>
        {fmt.number(file.sizeKb)} {kb}
      </span>
      <span className="w-[42px] shrink-0 text-right text-[9px] whitespace-nowrap" style={{ color: 'var(--au-text2)' }}>
        {fmt.dayMonth(file.day)}
      </span>
    </>
  )
}

/** A block's caption, with the count the application keeps on the right of it. */
function BlockLabel({ children, count }: { children: React.ReactNode; count: number }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span
        className="text-[8.5px] font-bold whitespace-nowrap"
        style={{ color: 'var(--au-text2)', letterSpacing: '0.02em' }}
      >
        {children}
      </span>
      <span className="text-[9px] font-bold whitespace-nowrap tabular-nums" style={{ color: 'var(--au-text2)' }}>
        {count}
      </span>
    </div>
  )
}

/**
 * Dossier · contacts, documents and log — the other half of the client record, so the CRM page does
 * not show the same grid of address fields twice. Contacts arrive first, then the documents filed
 * against the client, then the log; a beat later the newest entry drops in on top of the timeline,
 * which is what makes the dossier read as live rather than as an archive. Driven by `useScenario`'s
 * 9-beat loop; `beat === null` (SSR, no-JS, reduced motion) renders the resolved rest state.
 */
export function ClientLogVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = beat ?? REST_BEAT

  // Every block is present in every frame. What changes is the newest entry: the resting beat has
  // it and nothing marked, beat 1 is the dossier as it stood before it, and the two after that land
  // it and then point it at the document it is about. Both marks clear on the wrap.
  const fileIn = effBeat === REST_BEAT || effBeat >= FILE_LANDS_AT
  const entryIn = effBeat === REST_BEAT || effBeat >= ENTRY_LANDS_AT
  // One pair is lit at a time, and which one moves down the card. Both clear on the wrap.
  const litFile =
    effBeat === SECOND_LINK_AT ? SECOND_LINKED?.file : effBeat >= FILE_LANDS_AT ? NEWEST_LOG.file : undefined
  const litEntry =
    effBeat === SECOND_LINK_AT ? SECOND_LINKED?.file : effBeat >= ENTRY_LANDS_AT ? NEWEST_LOG.file : undefined
  // The counts the app keeps beside a block heading, and they move with what is under them.
  const fileCount = CLIENT_FILES.length - (fileIn ? 0 : 1)
  const logCount = CLIENT_LOG.length - (entryIn ? 0 : 1)

  const logEntry = (key: (typeof CLIENT_LOG)[number]['key'], initials: (typeof CLIENT_LOG)[number]['member']) =>
    t.screen.clientLog.entry[key](teamMember(initials).firstName)

  return (
    <AppUiVisual label={t.screen.clientLog.caption(SUBJECT.name)} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="client-log" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.word.clients}
            detail={`${SUBJECT.nr} · ${SUBJECT.name}`}
            right={<Tag>{SUBJECT.form}</Tag>}
          />

          {/* The room the contacts block used to take goes into the two that are left: at these
              gaps the shelf and the log fill the card between them, where a tighter set left a
              third of it empty under the last entry. */}
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-3 py-3">
            <div className="flex flex-col gap-2.5">
              <BlockLabel count={fileCount}>{t.screen.clientLog.files}</BlockLabel>
              {/* The uploaded document opens onto the shelf and pushes the two under it down; the
                  other two are always there. */}
              {CLIENT_FILES.map((file, index) =>
                file.name === NEWEST_LOG.file ? (
                  <div key={file.name} className={clsx(styles.arriving, fileIn && styles.arrivingIn)}>
                    <div className={clsx(styles.fileRow, file.name === litFile && styles.marked)}>
                      <FileRow file={file} fmt={fmt} kb={t.screen.clientLog.kb} />
                    </div>
                  </div>
                ) : (
                  <div
                    key={file.name}
                    className={clsx(
                      styles.fileRow,
                      styles.reveal,
                      styles.revealVisible,
                      file.name === litFile && styles.marked,
                    )}
                    style={{ transitionDelay: `${index * 80}ms` }}
                  >
                    <FileRow file={file} fmt={fmt} kb={t.screen.clientLog.kb} />
                  </div>
                ),
              )}
            </div>

            <div
              // The timeline shares out whatever the shelf above it leaves, so the card is filled
              // between the two blocks rather than ending a third of the way up from the bottom.
              className="flex min-h-0 flex-1 flex-col justify-between gap-1.5 border-t pt-4 pb-1"
              style={{ borderColor: 'var(--au-hairline)' }}
            >
              <BlockLabel count={logCount}>{t.screen.clientLog.log}</BlockLabel>
              {/* `shrink-0` on every entry: the log is the last block in a fixed-height card, and
                  without it flex takes the height it needs out of these rows and crops the text. */}
              <div className={clsx(styles.arriving, entryIn && styles.arrivingIn)}>
                <div
                  className={clsx(
                    'flex shrink-0 items-center gap-2',
                    styles.logRow,
                    NEWEST_LOG.file === litEntry && styles.marked,
                  )}
                >
                  <LogAvatar initials={NEWEST_LOG.member} />
                  <span className="min-w-0 flex-1 truncate text-[10px]">
                    {logEntry(NEWEST_LOG.key, NEWEST_LOG.member)}
                  </span>
                  <span
                    className="w-[42px] shrink-0 text-right text-[9px] whitespace-nowrap"
                    style={{ color: 'var(--au-text2)' }}
                  >
                    {fmt.dayMonth(NEWEST_LOG.day)}
                  </span>
                </div>
              </div>
              {SETTLED_LOG.map((entry, index) => (
                <div
                  key={entry.key}
                  className={clsx(
                    'flex shrink-0 items-center gap-2',
                    styles.logRow,
                    styles.reveal,
                    styles.revealVisible,
                    index >= NARROW_ENTRIES - 1 && styles.hideNarrow,
                    entry.file !== undefined && entry.file === litEntry && styles.marked,
                  )}
                  style={{ transitionDelay: `${index * 80}ms` }}
                >
                  <LogAvatar initials={entry.member} />
                  <span className="min-w-0 flex-1 truncate text-[10px]">{logEntry(entry.key, entry.member)}</span>
                  <span
                    className="w-[42px] shrink-0 text-right text-[9px] whitespace-nowrap"
                    style={{ color: 'var(--au-text2)' }}
                  >
                    {fmt.dayMonth(entry.day)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}

function LogAvatar({ initials }: { initials: (typeof CLIENT_LOG)[number]['member'] }) {
  const member = teamMember(initials)
  return (
    <span
      className="mt-px flex size-[16px] shrink-0 items-center justify-center rounded-full text-[6.5px] font-bold text-white"
      style={{ background: member.color }}
    >
      {member.initials}
    </span>
  )
}
